// Tela de boas-vindas multi-step pra explicar a plataforma na primeira vez
// que o aluno entra. Aciona em 2 momentos:
//   1. Auto-open: se users.prefs.welcomeSeenAt for null (primeiro login).
//   2. Manual: botão "Por onde começar" no header do Início.
//
// Ao fechar, persiste { welcomeSeenAt: ISO } em users.prefs preservando o
// resto. Decisão 2026-05-23.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  Sparkles,
  Database,
  BookOpen,
  PlayCircle,
  Bot,
  StickyNote,
  PenLine,
  Library,
  ChevronLeft,
  ChevronRight,
  X
} from "lucide-react";
import { api } from "./api";

export const welcomeOpen = signal<boolean>(false);

// VERSÃO do modal de boas-vindas. Bumpa quando adicionar/mudar conteúdo
// significativo: alunos que viram a versão anterior vão ver de novo a nova
// (sem incomodar quem nunca tinha visto, que abre normalmente).
//
// Histórico:
//   v1 — 2026-05-23: 6 slides (intro, bancos, estudo, prova, IA, cadernos)
//   v2 — 2026-05-23: +Redações com correção IA, +Biblioteca de Provas;
//                    cadernos reescrito com foco em IA.
const WELCOME_VERSION = 2;

// localStorage = primeira linha de defesa contra reabrir. Formato:
// "v{N}:{iso}". Sem prefixo "v" → assumimos v1 (legado pré-versionamento).
const LOCAL_SEEN_KEY = "student.welcome.seen";

function getLocalSeen(): { version: number; iso: string } | null {
  try {
    const raw = localStorage.getItem(LOCAL_SEEN_KEY);
    if (!raw) return null;
    const m = /^v(\d+):(.+)$/.exec(raw);
    if (m) return { version: parseInt(m[1], 10), iso: m[2] };
    // Legado sem versão = v1.
    return { version: 1, iso: raw };
  } catch {
    return null;
  }
}
function setLocalSeen(iso: string) {
  try {
    localStorage.setItem(LOCAL_SEEN_KEY, `v${WELCOME_VERSION}:${iso}`);
  } catch {
    /* localStorage disabled — sem persistência local, mas server cuida */
  }
}

/** Decide se deve abrir auto. Regra: abre quando a versão "vista" pelo aluno
 *  for menor que a versão atual do modal. Cobre: (a) primeiro acesso de
 *  todos, (b) alunos que viram a versão antiga e ganham os slides novos.
 *  Checa localStorage primeiro (rápido + zero race), depois cai pro server. */
export async function maybeOpenWelcomeFirstTime(): Promise<void> {
  if (welcomeOpen.value) return;
  const local = getLocalSeen();
  if (local && local.version >= WELCOME_VERSION) return;

  let prefs: Record<string, unknown> | null = null;
  try {
    const r = await api.getPrefs();
    prefs = r.prefs;
  } catch (e) {
    console.warn("[welcome] check prefs falhou:", e);
    return;
  }
  const serverSeenVersion =
    prefs && typeof prefs === "object" &&
    typeof (prefs as Record<string, unknown>).welcomeSeenVersion === "number"
      ? ((prefs as Record<string, unknown>).welcomeSeenVersion as number)
      : prefs && typeof prefs === "object" &&
        typeof (prefs as Record<string, unknown>).welcomeSeenAt === "string"
        ? 1 // legado sem versão = v1
        : 0;
  if (serverSeenVersion >= WELCOME_VERSION) {
    // Server confirma que já viu essa versão — sincroniza cache local.
    const iso =
      ((prefs as Record<string, unknown>).welcomeSeenAt as string) ?? new Date().toISOString();
    setLocalSeen(iso);
    return;
  }
  welcomeOpen.value = true;
}

/** Abertura manual (botão "Por onde começar"). Sempre abre. */
export function openWelcome() {
  welcomeOpen.value = true;
}

export function closeWelcome() {
  welcomeOpen.value = false;
}

interface Step {
  icon: any;
  title: string;
  body: string;
  hint?: string;
}

const STEPS: Step[] = [
  {
    icon: Sparkles,
    title: "Bem-vindo ao Simulador",
    body:
      "Essa plataforma reúne bancos de questões, simulados cronometrados, redações dissertativas, anotações organizadas em cadernos e um tutor IA que te explica cada questão na hora.",
    hint: "Em 1 minuto você vê tudo. Use as setas pra navegar."
  },
  {
    icon: Database,
    title: "Bancos de questões",
    body:
      "Cada banco é uma coleção de questões (de múltipla escolha, dissertativas ou as duas). Você acessa pela sidebar em \"Bancos\" e pode usar nos modos abaixo. Provas que o professor ou o admin compartilharem aparecem direto na sua lista.",
    hint: "O badge ao lado do nome do banco mostra quantas questões e que tipo são."
  },
  {
    icon: BookOpen,
    title: "Modo Estudo",
    body:
      "Você responde sem cronômetro e vê o feedback (acertou/errou + resolução) na hora. Útil pra aprender questão a questão sem pressão. Pode pausar, voltar, refazer — o progresso fica salvo.",
    hint: "O Modo Estudo NÃO compartilha respostas com a Simulação — cada modo é independente."
  },
  {
    icon: PlayCircle,
    title: "Modo Simulação (Prova)",
    body:
      "Você responde com cronômetro, sem feedback no meio. Quando finaliza, o sistema mostra resultado com nota, tempo por questão e revisão completa. Versões dissertativas geradas por IA também rodam aqui — a IA corrige todas as redações ao final automaticamente.",
    hint: "Pode pausar a qualquer momento. Se fechar a aba acidentalmente, retoma de onde parou."
  },
  {
    icon: Bot,
    title: "Tutor IA",
    body:
      "Em qualquer questão do Modo Estudo, abre a sidebar do Tutor IA pra perguntar o que quiser: \"por que essa alternativa está errada?\", \"me explica esse conceito como se eu fosse leigo\", \"me dá um exemplo prático\". A IA muda de persona conforme o tema (cardio, infectologia, etc).",
    hint: "Tem botão pra abrir o tutor em janela separada se você quiser estudar ao lado de outra tela."
  },
  {
    icon: StickyNote,
    title: "Cadernos com IA",
    body:
      "Você cria cadernos de estudo e anexa o que quiser como fonte — PDFs de aulas, links de artigos, suas próprias anotações. A IA lê tudo isso e fica disponível pra te ajudar a entender o conteúdo no todo: pergunta o que quiser sobre o material, peça resumos, briefings, glossário, audio-overview narrado ou até gerar um banco de questões novo a partir do que está lá dentro.",
    hint: "Acesse em \"Estudando com IA\" na sidebar. Cadernos podem ser compartilhados com colegas."
  },
  {
    icon: PenLine,
    title: "Redações com correção IA",
    body:
      "Pratique redação com temas reais das principais bancas (ENEM, FUVEST, UNICAMP, UNESP, ITA, vestibulares e concursos). Você escolhe a banca + ano, escreve sua redação no padrão dela (linhas contadas, tempo opcional), e ao terminar a IA corrige seguindo os critérios oficiais daquela banca — competências, repertório, proposta de intervenção, tudo.",
    hint: "Acesse em \"Redações\" na sidebar. Temas novos entram conforme as bancas publicam."
  },
  {
    icon: Library,
    title: "Biblioteca de Provas",
    body:
      "Coleção curada de provas oficiais (medicina, magistratura, vestibulares, concursos) + temas de redação prontos pra usar. Você navega pelas pastas, abre a prova que quiser e já entra no Modo Estudo ou Simulação. Tudo público — não precisa pedir acesso, basta clicar.",
    hint: "Acesse em \"Biblioteca\" na sidebar. Novidades aparecem aqui antes."
  }
];

export function WelcomeModal() {
  const open = welcomeOpen.value;
  const [step, setStep] = useState(0);
  if (!open) return null;
  const total = STEPS.length;
  const cur = STEPS[step];
  const Icon = cur.icon;

  async function close() {
    welcomeOpen.value = false;
    setStep(0);
    const iso = new Date().toISOString();
    // Marca local PRIMEIRO — bloqueia reabertura imediata mesmo se o server
    // demorar/falhar. Save server depois pra persistir entre sessões/devices.
    setLocalSeen(iso);
    try {
      const { prefs } = await api.getPrefs();
      const next = {
        ...(prefs ?? {}),
        welcomeSeenAt: iso,
        welcomeSeenVersion: WELCOME_VERSION
      };
      await api.savePrefs(next);
    } catch (e) {
      console.warn("[welcome] save prefs falhou:", e);
    }
  }

  function next() {
    if (step < total - 1) setStep(step + 1);
    else void close();
  }
  function prev() {
    if (step > 0) setStep(step - 1);
  }

  // Setas do teclado pra navegar: ← volta, → avança, ESC fecha. Bind só
  // enquanto o modal está aberto.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        next();
      } else if (e.key === "Escape") {
        e.preventDefault();
        void close();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step]);

  return (
    <div
      class="fixed inset-0 z-[120] grid place-items-center bg-background/85 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) void close();
      }}
    >
      <div class="w-full max-w-lg overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/10 to-fuchsia-500/10 px-6 py-4">
          <div class="flex items-center gap-2">
            <div class="grid size-9 place-items-center rounded-md bg-primary/15 text-primary ring-1 ring-primary/30">
              <Icon className="size-5" />
            </div>
            <h2 id="welcome-title" class="text-base font-bold">
              {cur.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => void close()}
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>

        {/* Altura fixa pra acomodar o slide mais longo (Cadernos com IA).
            Sem isso, os botões "Próximo"/"Concluir" no rodapé mudam de posição
            entre slides e o aluno clica no nada. Pedido 2026-05-23. */}
        <div class="flex min-h-[260px] flex-col gap-3 px-6 py-6">
          <p class="text-sm leading-relaxed">{cur.body}</p>
          {cur.hint && (
            <p class="mt-auto text-xs italic text-muted-foreground">
              {cur.hint}
            </p>
          )}
        </div>

        <footer class="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-3">
          {/* Indicador de progresso (pontinhos) */}
          <div class="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setStep(i)}
                aria-label={`Ir para passo ${i + 1}`}
                class={`h-1.5 rounded-full transition-all ${
                  i === step
                    ? "w-6 bg-primary"
                    : "w-1.5 bg-muted-foreground/30 hover:bg-muted-foreground/60"
                }`}
              />
            ))}
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              onClick={prev}
              disabled={step === 0}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft className="size-3.5" />
              Voltar
            </button>
            <button
              type="button"
              onClick={next}
              class="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:brightness-110"
            >
              {step === total - 1 ? "Concluir" : "Próximo"}
              {step < total - 1 && <ChevronRight className="size-3.5" />}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
